import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, X } from 'lucide-react';

const STAGE_COLORS = [
  '#4A9D6E', '#D4855A', '#E5A84B', '#3E8A6E', '#6B7280', '#1A1D21',
  '#7C3AED', '#E55B5B', '#0891B2', '#65A30D'
];

const PRESET_STAGES = [
  { name: 'Design', color: '#4A9D6E' },
  { name: 'Ready to Cut', color: '#D4855A' },
  { name: 'Ready for Paint', color: '#E5A84B' },
  { name: 'Ready for Assembly', color: '#3E8A6E' },
  { name: 'Install', color: '#6B7280' },
];

function StageChip({ stage, index, onRemove, canRemove }) {
  return (
    <div className="flex items-center gap-2 bg-[#1A1D21] border border-[#33383F] rounded-xl px-3 py-2"
      data-testid={`stage-chip-${index}`}>
      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
      <span className="flex-1 text-sm text-[#F0EDE8] font-medium">{stage.name}</span>
      <span className="text-xs text-[#9CA3AF]">{index + 1}</span>
      {canRemove && (
        <Button type="button" size="icon" variant="ghost"
          className="h-6 w-6 text-[#E55B5B] hover:text-[#E55B5B] hover:bg-[#E55B5B]/10 rounded-lg"
          onClick={() => onRemove(index)}
          data-testid={`stage-remove-${index}`}>
          <X className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
}

export function StageEditor({ stages, onChange }) {
  const [newStageName, setNewStageName] = useState('');

  // Available preset stages not already added
  const availablePresets = PRESET_STAGES.filter(
    preset => !stages.some(s => s.name === preset.name)
  );

  const handleAddPreset = (name) => {
    const preset = PRESET_STAGES.find(p => p.name === name);
    if (!preset) return;
    const newStage = {
      id: `new-${Date.now()}`,
      name: preset.name,
      order: stages.length,
      color: preset.color
    };
    onChange([...stages, newStage]);
  };

  const handleAddCustom = () => {
    if (!newStageName.trim()) return;
    const colorIdx = stages.length % STAGE_COLORS.length;
    const newStage = {
      id: `custom-${Date.now()}`,
      name: newStageName.trim(),
      order: stages.length,
      color: STAGE_COLORS[colorIdx]
    };
    onChange([...stages, newStage]);
    setNewStageName('');
  };

  const handleRemove = (index) => {
    const updated = stages.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i }));
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <Label className="text-[#F0EDE8] font-medium">Project Stages</Label>

      {/* Current stages list */}
      <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
        {stages.map((stage, i) => (
          <StageChip key={stage.id || i} stage={stage} index={i}
            onRemove={handleRemove} canRemove={stages.length > 1} />
        ))}
      </div>

      {/* Add from dropdown */}
      {availablePresets.length > 0 && (
        <Select onValueChange={handleAddPreset}>
          <SelectTrigger className="bg-[#1A1D21] border-[#33383F] text-[#9CA3AF] rounded-xl" data-testid="add-stage-dropdown">
            <SelectValue placeholder="Add a stage..." />
          </SelectTrigger>
          <SelectContent className="bg-[#23272E] border-[#33383F]">
            {availablePresets.map((preset) => (
              <SelectItem key={preset.name} value={preset.name} className="text-[#F0EDE8] focus:bg-[#2C3038] focus:text-[#F0EDE8]">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: preset.color }} />
                  {preset.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Add custom stage */}
      <div className="flex gap-2">
        <Input value={newStageName} onChange={(e) => setNewStageName(e.target.value)}
          placeholder="Or type a custom stage..."
          className="flex-1 h-9 text-sm bg-[#1A1D21] border-[#33383F] text-[#F0EDE8] placeholder:text-[#555D68] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl"
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustom())}
          data-testid="new-stage-input" />
        <Button type="button" size="sm" variant="outline" onClick={handleAddCustom}
          className="h-9 border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl"
          data-testid="add-stage-button">
          <Plus className="w-3 h-3 mr-1" />Add
        </Button>
      </div>
    </div>
  );
}
